import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  PieChart,
  Download,
} from 'lucide-react';

interface CostPeriod {
  period: string;
  usage_count: number;
  total_quantity: number;
  total_cost: number;
  total_chip_time: number;
}

interface CostByJob {
  job_id: string;
  job_number: string;
  customer?: string;
  part_number?: string;
  status: string;
  usage_count: number;
  total_quantity: number;
  total_cost: number;
  total_chip_time: number;
}

interface CostByTool {
  definition_id: string;
  name: string;
  part_number?: string;
  manufacturer?: string;
  unit_cost?: number;
  usage_count: number;
  total_quantity: number;
  total_cost: number;
  total_chip_time: number;
  avg_life_hours?: number;
}

export function CostAnalysis() {
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Cost over time
  const { data: costOverTime, isLoading: loadingOverTime } = useQuery({
    queryKey: ['cost-analysis', groupBy, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ groupBy });
      if (startDate) params.set('startDate', new Date(startDate).toISOString());
      if (endDate) params.set('endDate', new Date(endDate).toISOString());

      const res = await api.get<CostPeriod[]>(`/reports/cost-analysis?${params}`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Cost by job
  const { data: costByJob, isLoading: loadingByJob } = useQuery({
    queryKey: ['cost-by-job'],
    queryFn: async () => {
      const res = await api.get<CostByJob[]>('/reports/cost-by-job');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Cost by tool
  const { data: costByTool, isLoading: loadingByTool } = useQuery({
    queryKey: ['cost-by-tool'],
    queryFn: async () => {
      const res = await api.get<CostByTool[]>('/reports/cost-by-tool');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Calculate totals
  const totalCost = costOverTime?.reduce((sum, p) => sum + (p.total_cost || 0), 0) || 0;
  const totalQuantity = costOverTime?.reduce((sum, p) => sum + (p.total_quantity || 0), 0) || 0;
  const totalChipTime = costOverTime?.reduce((sum, p) => sum + (p.total_chip_time || 0), 0) || 0;

  // Calculate trend (compare last two periods)
  const trend = costOverTime && costOverTime.length >= 2
    ? ((costOverTime[costOverTime.length - 1].total_cost || 0) - (costOverTime[costOverTime.length - 2].total_cost || 0))
    : 0;

  const exportToCSV = () => {
    if (!costOverTime) return;

    const headers = ['Period', 'Usage Count', 'Quantity', 'Cost', 'Chip Time (hrs)'];
    const rows = costOverTime.map(p => [
      p.period,
      p.usage_count,
      p.total_quantity,
      p.total_cost?.toFixed(2) || '0',
      p.total_chip_time?.toFixed(1) || '0',
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-analysis-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cost Analysis</h1>
          <p className="text-foreground-muted">Analyze tool usage costs and trends</p>
        </div>
        <Button onClick={exportToCSV}>
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Total Cost</p>
              <p className="text-2xl font-bold">{formatCurrency(totalCost)}</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-info/10 rounded-lg">
              <BarChart3 className="h-6 w-6 text-info" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Tools Used</p>
              <p className="text-2xl font-bold">{totalQuantity}</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-warning/10 rounded-lg">
              <Calendar className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Total Chip Time</p>
              <p className="text-2xl font-bold">{totalChipTime.toFixed(1)}h</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${trend >= 0 ? 'bg-error/10' : 'bg-success/10'}`}>
              {trend >= 0 ? (
                <TrendingUp className="h-6 w-6 text-error" />
              ) : (
                <TrendingDown className="h-6 w-6 text-success" />
              )}
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Trend</p>
              <p className={`text-2xl font-bold ${trend >= 0 ? 'text-error' : 'text-success'}`}>
                {trend >= 0 ? '+' : ''}{formatCurrency(trend)}
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Group By</label>
              <Select
                options={[
                  { value: 'day', label: 'Daily' },
                  { value: 'week', label: 'Weekly' },
                  { value: 'month', label: 'Monthly' },
                ]}
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
                className="w-32"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
            >
              Clear Dates
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Cost Over Time */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Cost Over Time
          </CardTitle>
        </CardHeader>
        <CardBody>
          {loadingOverTime ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : costOverTime?.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted">
              <PieChart className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No cost data</p>
              <p className="text-sm">Cost data will appear once tools are used on jobs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th className="text-right">Usage Count</th>
                    <th className="text-right">Quantity</th>
                    <th className="text-right">Chip Time</th>
                    <th className="text-right">Total Cost</th>
                    <th className="text-right">Cost/Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {costOverTime?.map((period) => (
                    <tr key={period.period}>
                      <td className="font-medium">{period.period}</td>
                      <td className="text-right">{period.usage_count || 0}</td>
                      <td className="text-right">{period.total_quantity || 0}</td>
                      <td className="text-right">
                        {period.total_chip_time ? `${period.total_chip_time.toFixed(1)}h` : '-'}
                      </td>
                      <td className="text-right font-medium">{formatCurrency(period.total_cost)}</td>
                      <td className="text-right text-foreground-muted">
                        {period.total_quantity
                          ? formatCurrency((period.total_cost || 0) / period.total_quantity)
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-border">
                  <tr className="font-bold">
                    <td>Total</td>
                    <td className="text-right">{costOverTime?.reduce((s, p) => s + (p.usage_count || 0), 0)}</td>
                    <td className="text-right">{totalQuantity}</td>
                    <td className="text-right">{totalChipTime.toFixed(1)}h</td>
                    <td className="text-right">{formatCurrency(totalCost)}</td>
                    <td className="text-right text-foreground-muted">
                      {totalQuantity ? formatCurrency(totalCost / totalQuantity) : '-'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Top Costly Jobs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Top Costly Jobs
          </CardTitle>
        </CardHeader>
        <CardBody>
          {loadingByJob ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : costByJob?.length === 0 ? (
            <div className="text-center py-8 text-foreground-muted">
              <p>No job cost data available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th className="text-right">Tools Used</th>
                    <th className="text-right">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costByJob?.slice(0, 10).map((job) => (
                    <tr key={job.job_id}>
                      <td>
                        <div>
                          <p className="font-medium">{job.job_number}</p>
                          {job.part_number && (
                            <p className="text-sm text-foreground-muted">{job.part_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="text-foreground-muted">{job.customer || '-'}</td>
                      <td>
                        <Badge
                          variant={
                            job.status === 'completed'
                              ? 'success'
                              : job.status === 'active'
                              ? 'primary'
                              : 'neutral'
                          }
                        >
                          {job.status}
                        </Badge>
                      </td>
                      <td className="text-right">{job.total_quantity || 0}</td>
                      <td className="text-right font-medium">{formatCurrency(job.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Top Costly Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top Costly Tools
          </CardTitle>
        </CardHeader>
        <CardBody>
          {loadingByTool ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : costByTool?.length === 0 ? (
            <div className="text-center py-8 text-foreground-muted">
              <p>No tool cost data available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th className="text-right">Unit Cost</th>
                    <th className="text-right">Qty Used</th>
                    <th className="text-right">Avg Life</th>
                    <th className="text-right">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costByTool?.slice(0, 10).map((tool) => (
                    <tr key={tool.definition_id}>
                      <td>
                        <div>
                          <p className="font-medium">{tool.name}</p>
                          {tool.part_number && (
                            <p className="text-sm text-foreground-muted">{tool.part_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="text-right">{formatCurrency(tool.unit_cost)}</td>
                      <td className="text-right">{tool.total_quantity || 0}</td>
                      <td className="text-right">
                        {tool.avg_life_hours ? `${tool.avg_life_hours.toFixed(1)}h` : '-'}
                      </td>
                      <td className="text-right font-medium">{formatCurrency(tool.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
